import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, TextField, InputAdornment,
  CircularProgress, Alert, Select, FormControl, InputLabel, MenuItem,
  Stack, Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon, Edit as EditIcon, Close as CloseIcon,
  Add as AddIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import iwalkcvService, { IWGuia } from '../../services/iwalkcv.service';

const IDIOMAS = ['Português', 'Crioulo', 'Inglês', 'Francês', 'Espanhol', 'Alemão', 'Italiano'];

const EMPTY: any = {
  nome: '', foto_url: '', telefone: '', email: '', whatsapp: '', bio: '',
  ilha_id: '', idiomas: [], areas_atuacao: '', tipo_experiencias: '',
  certificacoes: '', anos_experiencia: '', nif: '', dados_pagamento: '',
  status: 'ACTIVE', experiencia_ids: [],
};

interface Ilha { id: number; nome: string; }
interface Exp { id: number; titulo: string; }

export default function GuiasPage() {
  const [guias, setGuias] = useState<IWGuia[]>([]);
  const [ilhas, setIlhas] = useState<Ilha[]>([]);
  const [experiencias, setExperiencias] = useState<Exp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<any>(EMPTY);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await iwalkcvService.getGuias();
      setGuias(res.data || []);
    } catch {
      setError('Erro ao carregar guias');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    iwalkcvService.getIlhas().then((r) => setIlhas(r.data || [])).catch(() => {});
    iwalkcvService.getExperiencias({ limit: 1000 }).then((r) => setExperiencias(r.data || [])).catch(() => {});
  }, []);

  const filtered = guias.filter((g) => !search || g.nome.toLowerCase().includes(search.toLowerCase()));

  function openCreate() {
    setForm(EMPTY); setEditId(null); setSaveError(null); setDialogOpen(true);
  }

  async function openEdit(id: number) {
    setSaveError(null);
    try {
      const res = await iwalkcvService.getGuia(id);
      const g = res.data;
      setForm({
        ...EMPTY, ...g,
        ilha_id: g.ilha_id || '',
        anos_experiencia: g.anos_experiencia ?? '',
        idiomas: Array.isArray(g.idiomas) ? g.idiomas : [],
        experiencia_ids: g.experiencia_ids || [],
      });
      setEditId(id);
      setDialogOpen(true);
    } catch {
      setError('Erro ao carregar guia');
    }
  }

  async function handleSave() {
    if (!form.nome.trim()) { setSaveError('O nome é obrigatório'); return; }
    setSaving(true); setSaveError(null);
    try {
      const payload = {
        ...form,
        ilha_id: form.ilha_id || null,
        anos_experiencia: form.anos_experiencia === '' ? null : Number(form.anos_experiencia),
      };
      if (editId) await iwalkcvService.updateGuia(editId, payload);
      else await iwalkcvService.createGuia(payload);
      setDialogOpen(false);
      load();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try {
      await iwalkcvService.deleteGuia(deleteId);
      setDeleteId(null);
      load();
    } catch {
      setError('Erro ao eliminar guia');
    }
  }

  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Guias</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ bgcolor: '#8FA800', '&:hover': { bgcolor: '#7a9000' } }}>
          Novo guia
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TextField
        placeholder="Pesquisar por nome..." size="small" value={search}
        onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2, width: 320 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {['Nome', 'Ilha', 'Contactos', 'Idiomas', 'Experiências', 'Estado', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhum guia encontrado</TableCell></TableRow>
              ) : filtered.map((g) => (
                <TableRow key={g.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{g.nome}</TableCell>
                  <TableCell>{g.ilha_nome || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{g.telefone || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{g.email || ''}</Typography>
                  </TableCell>
                  <TableCell>{Array.isArray(g.idiomas) ? g.idiomas.join(', ') : '—'}</TableCell>
                  <TableCell>{g.total_experiencias ?? 0}</TableCell>
                  <TableCell>
                    <Chip size="small" label={g.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                      color={g.status === 'ACTIVE' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(g.id)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setDeleteId(g.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? 'Editar guia' : 'Novo guia'}
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Nome *" fullWidth value={form.nome} onChange={set('nome')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Ilha de residência</InputLabel>
                <Select label="Ilha de residência" value={form.ilha_id} onChange={set('ilha_id')}>
                  <MenuItem value="">—</MenuItem>
                  {ilhas.map((i) => <MenuItem key={i.id} value={i.id}>{i.nome}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}><TextField label="Telefone" fullWidth value={form.telefone} onChange={set('telefone')} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="WhatsApp" fullWidth value={form.whatsapp} onChange={set('whatsapp')} /></Grid>
            <Grid item xs={12} sm={4}><TextField label="Email" fullWidth value={form.email} onChange={set('email')} /></Grid>

            <Grid item xs={12}>
              <Autocomplete multiple freeSolo options={IDIOMAS} value={form.idiomas}
                onChange={(_, v) => setForm((f: any) => ({ ...f, idiomas: v }))}
                renderInput={(params) => <TextField {...params} label="Idiomas falados" placeholder="Adicionar idioma" />} />
            </Grid>

            <Grid item xs={12} sm={6}><TextField label="Áreas onde opera" fullWidth value={form.areas_atuacao} onChange={set('areas_atuacao')} placeholder="Ex: Sal, Boa Vista" /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Tipo de experiências" fullWidth value={form.tipo_experiencias} onChange={set('tipo_experiencias')} placeholder="Ex: Mergulho, trilhos" /></Grid>

            <Grid item xs={12} sm={4}><TextField label="Anos de experiência" type="number" fullWidth value={form.anos_experiencia} onChange={set('anos_experiencia')} /></Grid>
            <Grid item xs={12} sm={8}><TextField label="Certificações / licenças" fullWidth value={form.certificacoes} onChange={set('certificacoes')} /></Grid>

            <Grid item xs={12} sm={6}><TextField label="NIF" fullWidth value={form.nif} onChange={set('nif')} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Dados de pagamento (IBAN/notas)" fullWidth value={form.dados_pagamento} onChange={set('dados_pagamento')} /></Grid>

            <Grid item xs={12}><TextField label="Bio / descrição" fullWidth multiline rows={3} value={form.bio} onChange={set('bio')} /></Grid>
            <Grid item xs={12}><TextField label="URL da foto" fullWidth value={form.foto_url} onChange={set('foto_url')} /></Grid>

            <Grid item xs={12}>
              <Autocomplete multiple options={experiencias} getOptionLabel={(o) => o.titulo}
                value={experiencias.filter((e) => form.experiencia_ids.includes(e.id))}
                onChange={(_, v) => setForm((f: any) => ({ ...f, experiencia_ids: v.map((x) => x.id) }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Experiências associadas" placeholder="Ligar a experiências" />} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select label="Estado" value={form.status} onChange={set('status')}>
                  <MenuItem value="ACTIVE">Ativo</MenuItem>
                  <MenuItem value="INACTIVE">Inativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#5B2D8E', '&:hover': { bgcolor: '#4a2472' } }}>
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar guia</DialogTitle>
        <DialogContent><Typography>Tem a certeza que quer eliminar este guia? Esta ação não pode ser desfeita.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
